import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Play, ExternalLink, Moon, Sun, Check, CheckCircle, ChevronLeft, ChevronRight, FileText, Circle, Share2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useDarkMode } from "../contexts/DarkModeContext";
import ReactMarkdown from 'react-markdown';
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Content Security Functions - Frontend Decoding with UTF-8 support (Compatible version)
const decodeContent = (encodedString) => {
  try {
    // Reverse the obfuscation
    const base64String = encodedString.split('').reverse().join('');
    
    // Use fetch to decode Base64 with proper UTF-8 handling
    const binaryString = atob(base64String);
    
    // Convert binary string to UTF-8 using escape/unescape (more compatible)
    const utf8String = decodeURIComponent(escape(binaryString));
    
    // Parse JSON
    const contentDict = JSON.parse(utf8String);
    return contentDict;
  } catch (error) {
    console.error('Error decoding content:', error);
    
    // Fallback: try direct atob (for backward compatibility)
    try {
      const base64String = encodedString.split('').reverse().join('');
      const jsonString = atob(base64String);
      const contentDict = JSON.parse(jsonString);
      return contentDict;
    } catch (fallbackError) {
      console.error('Fallback decoding also failed:', fallbackError);
      throw error;
    }
  }
};

// Helper function to check if video is available (has valid videoId)
const isVideoAvailable = (video) => {
  return video?.videoId && video.videoId.trim() !== '';
};

const VolumePage = () => {
  const { volumeNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, userType, loading: authLoading } = useAuth();
  const { isDarkMode: darkMode, toggleDarkMode } = useDarkMode();
  
  // Get return URL from query params (for back navigation)
  const urlParams = new URLSearchParams(location.search);
  const returnUrl = urlParams.get('from');
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(-1);
  const [watchingVideo, setWatchingVideo] = useState("");
  const [mainFileContent, setMainFileContent] = useState(null);
  const [mainFileLoading, setMainFileLoading] = useState(false);
  const [mainFileError, setMainFileError] = useState("");
  const [optionalFileContent, setOptionalFileContent] = useState(null);
  const [optionalFileLoading, setOptionalFileLoading] = useState(false);
  const [textScale, setTextScale] = useState(1);
  const [expandedQuestions, setExpandedQuestions] = useState([]);
  
  // Personal Notes state
  const [noteText, setNoteText] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSuccess, setNoteSuccess] = useState(false);

  // Bible Reference Modal state
  const [bibleModalOpen, setBibleModalOpen] = useState(false);
  const [currentBibleReference, setCurrentBibleReference] = useState("");

  // Chapter Search state
  const [searchInput, setSearchInput] = useState("");
  const [searchError, setSearchError] = useState("");
  
  // Share state
  const [shareSuccess, setShareSuccess] = useState(false);
  const [shareError, setShareError] = useState("");
  const [shareCapabilities, setShareCapabilities] = useState({
    webShare: false,
    clipboard: false,
    browserName: 'Unknown'
  });

  // Detect browser capabilities on mount
  useEffect(() => {
    const detectCapabilities = () => {
      const hasWebShare = !!(navigator.share && navigator.canShare);
      const hasClipboard = !!(navigator.clipboard && window.isSecureContext);
      
      // Simple browser detection
      let browserName = 'Unknown';
      if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')) {
        browserName = 'Safari';
      } else if (navigator.userAgent.includes('Chrome')) {
        browserName = 'Chrome';
      } else if (navigator.userAgent.includes('Firefox')) {
        browserName = 'Firefox';
      } else if (navigator.userAgent.includes('Edge')) {
        browserName = 'Edge';
      }
      
      setShareCapabilities({
        webShare: hasWebShare,
        clipboard: hasClipboard,
        browserName
      });
    };
    
    detectCapabilities();
  }, []);

  // Function to open Bible reference modal
  const openBibleDialog = (reference) => {
    setCurrentBibleReference(reference);
    setBibleModalOpen(true);
  };

  // Function to close Bible reference modal
  const closeBibleDialog = () => {
    setBibleModalOpen(false);
    setCurrentBibleReference("");
  };

  // Function to handle chapter search
  const handleChapterSearch = () => {
    setSearchError("");
    
    if (!searchInput.trim()) {
      setSearchError("Por favor ingresa un número de índice (ej: 5)");
      return;
    }
    
    // Check if playlist is loaded and has items array
    if (!playlist || !playlist.items || !Array.isArray(playlist.items)) {
      setSearchError("Playlist no cargada. Por favor espera un momento e intenta de nuevo.");
      return;
    }
    
    // Parse the input as just an index number
    const targetIndex = parseInt(searchInput.trim());
    
    if (isNaN(targetIndex) || targetIndex <= 0) {
      setSearchError("Ingresa un número válido (ej: 5)");
      return;
    }
    
    // Search in the current playlist for the matching index
    const foundVideo = playlist.items.find(video => {
      const extracted = extractVolumeIndexFromTitle(video.title);
      return extracted && extracted.index === targetIndex;
    });
    
    if (foundVideo) {
      // Found the video - select it using the proper selectVideo function
      const foundIndex = playlist.items.findIndex(item => item.videoId === foundVideo.videoId);
      selectVideo(foundVideo, foundIndex);
      setSearchError("");
      
      // Small delay to allow state to update, then scroll to playlist item
      setTimeout(() => {
        // Scroll the selected item into view in the playlist sidebar
        const playlistItem = document.querySelector(`[data-video-id="${foundVideo.videoId}"]`);
        if (playlistItem) {
          playlistItem.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
          
          // Optional: Add a brief highlight effect
          playlistItem.style.transition = 'background-color 0.3s ease';
          const originalBg = playlistItem.style.backgroundColor;
          playlistItem.style.backgroundColor = '#8B5CF6'; // Purple highlight
          setTimeout(() => {
            playlistItem.style.backgroundColor = originalBg;
          }, 500);
        }
        
        // Also scroll to video player
        const videoSection = document.querySelector('.aspect-video');
        if (videoSection) {
          videoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      setSearchError("No Disponible");
    }
  };

  // Handle Enter key in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleChapterSearch();
    }
  };

  // Handle share chapter functionality with browser-aware detection
  const handleShareChapter = async () => {
    if (!selectedVideo) return;
    
    const volumeIndex = extractVolumeIndexFromTitle(selectedVideo.title);
    if (!volumeIndex) return;
    
    const shareUrl = `${window.location.origin}/volume/${volumeNumber}?chapter=${volumeIndex.index}`;
    const shareTitle = `Volumen ${volumeNumber} - Capítulo ${volumeIndex.index}`;
    
    // Array of inspirational sharing messages
    const shareMessages = [
      "📖 Este capítulo del Libro de Cielo 💫 toca el alma. Regístrate y léelo aquí:",
      "🔥 Este mensaje del Cielo transforma. Entra con tu cuenta y léelo:",
      "✨ Vale la pena. Jesús habla directo al corazón en este capítulo. Solo regístrate:"
    ];
    
    // Select a random message
    const randomMessage = shareMessages[Math.floor(Math.random() * shareMessages.length)];
    
    // Update meta tags dynamically for better social sharing
    const updateMetaTags = () => {
      // Update Open Graph title
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute('content', shareTitle);
      }
      
      // Update Open Graph description  
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription) {
        ogDescription.setAttribute('content', `${randomMessage} - ${selectedVideo.title}`);
      }
      
      // Update Open Graph image
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) {
        ogImage.setAttribute('content', selectedVideo.thumbnail);
      }
      
      // Update Open Graph URL
      let ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.setAttribute('content', shareUrl);
      }
      
      // Update page title temporarily
      document.title = shareTitle;
    };
    
    // Update meta tags before sharing
    updateMetaTags();
    
    // 1. Try Web Share API with thumbnail image (best experience on mobile)
    if (navigator.share && navigator.canShare) {
      try {
        // Try to fetch and share the thumbnail image
        const response = await fetch(selectedVideo.thumbnail);
        const blob = await response.blob();
        const imageFile = new File([blob], `volumen-${volumeNumber}-capitulo-${volumeIndex.index}.jpg`, { 
          type: blob.type || 'image/jpeg' 
        });
        
        const shareDataWithImage = {
          title: shareTitle,
          text: randomMessage,
          url: shareUrl,
          files: [imageFile]
        };
        
        // Check if we can share with files
        if (navigator.canShare(shareDataWithImage)) {
          await navigator.share(shareDataWithImage);
          setShareSuccess(true);
          setTimeout(() => setShareSuccess(false), 3000);
          return;
        }
      } catch (imageErr) {
        console.log('Failed to share with image, trying without:', imageErr);
      }
      
      // Fallback: Share without image
      try {
        const shareData = {
          title: shareTitle,
          text: randomMessage,
          url: shareUrl
        };
        
        if (navigator.canShare(shareData)) {
          await navigator.share(shareData);
          setShareSuccess(true);
          setTimeout(() => setShareSuccess(false), 3000);
          return;
        }
      } catch (err) {
        console.log('Web Share cancelled or failed:', err);
      }
    }
    
    // 2. Enhanced clipboard copy with thumbnail (fallback)
    try {
      const enhancedShareText = [
        shareTitle,
        randomMessage,
        '',
        `🔗 ${shareUrl}`,
        `🖼️ Miniatura: ${selectedVideo.thumbnail}`,
        '',
        'Para WhatsApp: Copia el enlace y envía también la imagen de la miniatura'
      ].join('\n');
      
      await navigator.clipboard.writeText(enhancedShareText);
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 4000);
      return;
    } catch (clipboardErr) {
      console.log('Clipboard API failed:', clipboardErr);
    }
    
    // 3. Last resort: Manual copy via text selection (old browsers)
    fallbackCopyToClipboard(shareUrl, shareTitle, selectedVideo.thumbnail, randomMessage);
  };
  
  // Fallback copy method for older browsers
  const fallbackCopyToClipboard = (url, title, thumbnail, message = '') => {
    const textArea = document.createElement('textarea');
    const fallbackText = [
      title,
      message,
      '',
      `🔗 ${url}`,
      `🖼️ ${thumbnail}`
    ].join('\n');
    
    textArea.value = fallbackText;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 3000);
    } catch (err) {
      console.error('Fallback copy failed:', err);
      // Show manual copy instructions
      setShareError('No se pudo copiar automáticamente. Copia manualmente: ' + url);
      setTimeout(() => setShareError(""), 5000);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  // Function to toggle question expansion
  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => 
      prev.includes(questionId) 
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  // Handle topic click - navigate to tema detail page
  const handleTopicClick = async (topicTitle) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/temas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Find tema with matching title (case-insensitive)
      const matchingTema = response.data.temas?.find(tema => 
        tema.title.toLowerCase() === topicTitle.toLowerCase()
      );
      
      // Get current chapter from URL parameters OR selectedVideo
      const urlParams = new URLSearchParams(window.location.search);
      let currentChapter = urlParams.get('chapter');
      
      // If no URL parameter, get from selectedVideo
      if (!currentChapter && selectedVideo) {
        const volumeIndex = extractVolumeIndexFromTitle(selectedVideo.title);
        currentChapter = volumeIndex?.index || '1';
      }
      
      // Fallback to chapter 1
      currentChapter = currentChapter || '1';
      
      const returnUrl = `/volume/${volumeNumber}?chapter=${currentChapter}`;
      
      if (matchingTema) {
        navigate(`/tema/${matchingTema.id}?from=${encodeURIComponent(returnUrl)}`);
      } else {
        // If no exact match, go to temas page with return URL for user to search
        navigate(`/temas?from=${encodeURIComponent(returnUrl)}`);
      }
    } catch (error) {
      console.error('Error finding tema:', error);
      // Fallback: go to temas page with return URL
      const urlParams = new URLSearchParams(window.location.search);
      let currentChapter = urlParams.get('chapter');
      
      // If no URL parameter, get from selectedVideo
      if (!currentChapter && selectedVideo) {
        const volumeIndex = extractVolumeIndexFromTitle(selectedVideo.title);
        currentChapter = volumeIndex?.index || '1';
      }
      
      // Fallback to chapter 1
      currentChapter = currentChapter || '1';
      
      const returnUrl = `/volume/${volumeNumber}?chapter=${currentChapter}`;
      navigate(`/temas?from=${encodeURIComponent(returnUrl)}`);
    }
  };

  // Dynamic Type detection
  useEffect(() => {
    const detectTextScale = () => {
      // Create a test element to measure the actual rendered size
      const testElement = document.createElement('div');
      testElement.style.cssText = `
        position: absolute;
        visibility: hidden;
        font-size: 1rem;
        width: auto;
        height: auto;
        white-space: nowrap;
      `;
      testElement.textContent = 'Test';
      document.body.appendChild(testElement);
      
      const computedSize = window.getComputedStyle(testElement).fontSize;
      const actualSize = parseFloat(computedSize);
      const expectedSize = 16; // 1rem = 16px by default
      const scale = actualSize / expectedSize;
      
      document.body.removeChild(testElement);
      
      if (scale !== textScale) {
        setTextScale(scale);
        // Update CSS custom property for dynamic scaling
        document.documentElement.style.setProperty('--text-scale', scale.toString());
      }
    };

    // Detect initial scale
    detectTextScale();

    // Listen for resize events (which can indicate text size changes)
    const handleResize = () => {
      setTimeout(detectTextScale, 100);
    };

    window.addEventListener('resize', handleResize);
    
    // Also listen for zoom changes
    let lastZoom = window.devicePixelRatio;
    const checkZoom = () => {
      if (window.devicePixelRatio !== lastZoom) {
        lastZoom = window.devicePixelRatio;
        detectTextScale();
      }
    };
    
    const zoomInterval = setInterval(checkZoom, 500);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearInterval(zoomInterval);
    };
  }, [textScale]);

  useEffect(() => {
    // Wait for auth context to finish loading before checking authentication
    if (authLoading) {
      return; // Don't do anything while auth is still loading
    }

    // Redirect if not authenticated
    if (!isAuthenticated) {
      // Preserve full URL including chapter parameters for post-login redirect
      const currentUrl = `${location.pathname}${location.search}`;
      navigate(`/user-login?redirect=${encodeURIComponent(currentUrl)}`);
      return;
    }

    const fetchPlaylist = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/volumes/${volumeNumber}/playlist`);
        setPlaylist(response.data);
        setError("");
      } catch (err) {
        console.error("Error fetching playlist:", err);
        if (err.response?.status === 404) {
          setError("No se encontró contenido detallado para este volumen");
        } else if (err.response?.status === 401) {
          setError("Necesitas iniciar sesión para ver este contenido");
          // Preserve full URL including chapter parameters for post-login redirect
          const currentUrl = `${location.pathname}${location.search}`;
          navigate(`/user-login?redirect=${encodeURIComponent(currentUrl)}`);
        } else {
          setError("Error cargando el contenido del volumen");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPlaylist();
  }, [volumeNumber, isAuthenticated, authLoading, navigate]);

  // Auto-search effect when arriving from homepage search OR URL parameters
  useEffect(() => {
    // Check for search state from navigation OR URL parameters
    const searchIndex = location.state?.searchIndex;
    const urlParams = new URLSearchParams(location.search);
    const urlChapter = urlParams.get('chapter');
    
    // Use URL parameter if present, otherwise use navigation state
    const targetIndex = urlChapter ? parseInt(urlChapter) : searchIndex;
    
    if (targetIndex && playlist && playlist.items && !loading) {
      // Auto-execute search after playlist is loaded
      setSearchInput(targetIndex.toString());
      setSearchError(""); // Clear any previous errors
      
      // Search for the target index
      const foundVideo = playlist.items.find(video => {
        const extracted = extractVolumeIndexFromTitle(video.title);
        return extracted && extracted.index === targetIndex;
      });
      
      if (foundVideo) {
        // Found the video - select it using the proper selectVideo function
        const foundIndex = playlist.items.findIndex(item => item.videoId === foundVideo.videoId);
        selectVideo(foundVideo, foundIndex);
        setSearchError("");
        
        // Clean up URL parameters after successful load (keep clean URLs)
        if (urlChapter) {
          const cleanUrl = `${location.pathname}`;
          navigate(cleanUrl, { replace: true, state: {} });
        }
        
        // Scroll to video section after a brief delay
        setTimeout(() => {
          const videoSection = document.querySelector('.aspect-video');
          if (videoSection) {
            videoSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          
          // Show success message
          const tempSuccess = document.createElement('div');
          tempSuccess.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
          tempSuccess.textContent = `✓ Capítulo ${targetIndex} encontrado`;
          document.body.appendChild(tempSuccess);
          
          setTimeout(() => {
            if (document.body.contains(tempSuccess)) {
              document.body.removeChild(tempSuccess);
            }
          }, 3000);
        }, 500);
        
      } else {
        setSearchError(`Capítulo ${targetIndex} no disponible en este volumen`);
      }
      
      // Clear navigation state to prevent re-execution
      if (location.state?.searchIndex) {
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [playlist, loading, location.search, location.state, location.pathname, navigate]);

  // Shared function to handle video selection
  const selectVideo = (video, index) => {
    setSelectedVideo(video);
    setSelectedVideoIndex(index);
    
    // Extract volume and index from video title to fetch both main and optional content
    const volumeIndex = extractVolumeIndexFromTitle(video.title);
    if (volumeIndex) {
      fetchMainFileContent(volumeIndex.volume, volumeIndex.index);
      fetchOptionalFileContent(volumeIndex.volume, volumeIndex.index);
      fetchUserNote(volumeIndex.volume, volumeIndex.index);
    } else {
      setMainFileContent(null);
      setMainFileError("");
      setOptionalFileContent(null);
      setNoteText("");
      setNoteError("");
    }
  };

  // Function to extract volume and index from video title
  const extractVolumeIndexFromTitle = (title) => {
    // Look for pattern like "11-4" at the start of the title
    const match = title.match(/^(\d+)-(\d+)\./);
    if (match) {
      return {
        volume: parseInt(match[1]),
        index: parseInt(match[2])
      };
    }
    return null;
  };

  // Function to fetch main file content
  const fetchMainFileContent = async (volume, index) => {
    setMainFileLoading(true);
    setMainFileError("");
    setMainFileContent(null);

    try {
      const response = await axios.get(`${API}/main-file/${volume}/${index}`);
      
      // Decode the encoded content from the API
      if (response.data.encoded_content) {
        const decodedContent = decodeContent(response.data.encoded_content);
        setMainFileContent(decodedContent);
      } else {
        // Fallback for any legacy format
        setMainFileContent(response.data);
      }
    } catch (err) {
      console.error("Error fetching main file:", err);
      if (err.response?.status === 404) {
        // Don't show error for missing main files - just hide the section
        setMainFileError("");
      } else if (err.response?.status === 401) {
        // Session expired - the interceptor will handle logout, just show friendly message
        setMainFileError("Sesión expirada. Redirigiendo al login...");
      } else {
        setMainFileError("Error cargando el contenido principal");
      }
    } finally {
      setMainFileLoading(false);
    }
  };

  // Function to fetch optional file content
  const fetchOptionalFileContent = async (volume, index) => {
    setOptionalFileLoading(true);
    setOptionalFileContent(null);

    try {
      const response = await axios.get(`${API}/optional-file/${volume}/${index}`);
      
      // Decode the encoded content from the API
      if (response.data.encoded_content) {
        const decodedContent = decodeContent(response.data.encoded_content);
        setOptionalFileContent(decodedContent);
      } else {
        // Fallback for any legacy format
        setOptionalFileContent(response.data);
      }
    } catch (err) {
      console.error("Error fetching optional file:", err);
      // Silently fail - optional content is optional
      setOptionalFileContent(null);
    } finally {
      setOptionalFileLoading(false);
    }
  };

  // Navigation functions
  const goToPreviousVideo = () => {
    if (playlist?.items && selectedVideoIndex > 0) {
      const prevIndex = selectedVideoIndex - 1;
      const prevVideo = playlist.items[prevIndex];
      selectVideo(prevVideo, prevIndex);
      // Scroll to absolute top of page
      setTimeout(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        window.scrollTo(0, 0);
      }, 100);
    }
  };

  const goToNextVideo = () => {
    if (playlist?.items && selectedVideoIndex < playlist.items.length - 1) {
      const nextIndex = selectedVideoIndex + 1;
      const nextVideo = playlist.items[nextIndex];
      selectVideo(nextVideo, nextIndex);
      // Scroll to absolute top of page
      setTimeout(() => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        window.scrollTo(0, 0);
      }, 100);
    }
  };

  // Handle video click from list
  const handleVideoClick = (video) => {
    const index = playlist?.items?.findIndex(item => item.videoId === video.videoId) || 0;
    selectVideo(video, index);
  };

  const handleToggleWatched = async (video) => {
    // Prevent action if video is not available (empty videoId)
    if (!isVideoAvailable(video)) {
      console.log('Video not available for tracking - videoId is empty');
      return;
    }
    
    if (watchingVideo === video.videoId) return; // Prevent double clicks
    
    setWatchingVideo(video.videoId);
    
    try {
      const response = await axios.post(`${API}/videos/watch`, {
        video_id: video.videoId,
        volume_number: parseInt(volumeNumber)
      });

      // Update the playlist state locally
      setPlaylist(prev => ({
        ...prev,
        items: prev.items.map(item => 
          item.videoId === video.videoId 
            ? { ...item, is_watched: response.data.watched }
            : item
        )
      }));

      // If this is the currently selected video, update it too
      if (selectedVideo && selectedVideo.videoId === video.videoId) {
        setSelectedVideo(prev => ({ ...prev, is_watched: response.data.watched }));
      }

    } catch (err) {
      console.error("Error updating watch status:", err);
      if (err.response?.status === 401) {
        // Session expired - show toast notification
        const tempError = document.createElement('div');
        tempError.className = 'fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
        tempError.textContent = 'Sesión expirada. Redirigiendo al login...';
        document.body.appendChild(tempError);
        
        setTimeout(() => {
          if (document.body.contains(tempError)) {
            document.body.removeChild(tempError);
          }
        }, 3000);
      }
    } finally {
      setWatchingVideo("");
    }
  };

  // Personal Notes functions
  const fetchUserNote = async (volume, index) => {
    if (!isAuthenticated) return;
    
    setNoteLoading(true);
    setNoteError("");
    
    try {
      const response = await axios.get(`${API}/notes/${volume}/${index}`);
      setNoteText(response.data.note_text || "");
    } catch (error) {
      if (error.response?.status === 404) {
        // Note doesn't exist yet, that's fine
        setNoteText("");
      } else if (error.response?.status === 401) {
        // Session expired - the interceptor will handle logout
        setNoteError("Sesión expirada. Redirigiendo al login...");
      } else {
        console.error("Error fetching note:", error);
        setNoteError("Error al cargar la nota");
      }
    } finally {
      setNoteLoading(false);
    }
  };

  const saveUserNote = async () => {
    if (!isAuthenticated || !selectedVideo) return;
    
    const { volume, index } = extractVolumeIndexFromTitle(selectedVideo.title);
    if (!volume || !index) {
      setNoteError("No se pudo determinar el volumen y capítulo");
      return;
    }

    setNoteSaving(true);
    setNoteError("");
    setNoteSuccess(false);
    
    try {
      await axios.post(`${API}/notes/save?volume=${volume}&index=${index}`, {
        note_text: noteText
      });
      
      setNoteSuccess(true);
      setTimeout(() => setNoteSuccess(false), 3000); // Hide success message after 3 seconds
      
    } catch (error) {
      console.error("Error saving note:", error);
      if (error.response?.status === 401) {
        setNoteError("Sesión expirada. Redirigiendo al login...");
      } else {
        setNoteError("Error al guardar la nota");
      }
    } finally {
      setNoteSaving(false);
    }
  };

  const openYouTubeVideo = (video) => {
    if (video && video.url) {
      try {
        // Try to open in new window/tab
        const newWindow = window.open(video.url, '_blank', 'noopener,noreferrer');
        
        // Fallback if popup blocked
        if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
          console.log('Popup blocked, using location.href fallback');
          window.location.href = video.url;
        }
      } catch (error) {
        console.error('Error opening YouTube video:', error);
        // Last resort fallback
        window.location.href = video.url;
      }
    } else {
      console.error('Video or URL is missing:', video);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando contenido del volumen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-universal-title font-bold text-gray-800 mb-4">
              Volumen {volumeNumber}
            </h2>
            <p className="text-red-600 mb-6">{error}</p>
            
            {/* Donation message when content not found */}
            {error === "No se encontró contenido detallado para este volumen" && (
              <div className="mb-6 p-6 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-purple-800 mb-4 text-lg">
                  🙏 Ayúdanos a desarrollar esta página de la plataforma haciendo una donación aquí.
                </p>
                <a
                  href="https://donorbox.org/divinavoluntadapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 rounded-full text-base font-semibold transition-all duration-300 transform hover:scale-105 cursor-pointer bg-yellow-600 hover:bg-yellow-500 text-white shadow-lg hover:shadow-xl"
                >
                  <svg 
                    className="w-5 h-5 mr-2" 
                    fill="currentColor" 
                    viewBox="0 0 20 20" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                  </svg>
                  Dona aquí
                </a>
              </div>
            )}
            <div className="space-y-4">
              <button
                onClick={() => navigate(returnUrl || "/")}
                className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors duration-300 flex items-center justify-center"
              >
                <ArrowLeft size={20} className="mr-2" />
                {returnUrl ? 'Volver' : 'Volver al inicio'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while authentication state is being determined
  if (authLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
        darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      darkMode ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-900'
    }`}>
      {/* Header */}
      <header className={`${
        darkMode ? 'bg-purple-800' : 'bg-purple-600'
      } text-white py-6 shadow-lg transition-colors duration-300`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(returnUrl || "/")}
                className="flex items-center text-white hover:text-purple-200 transition-colors duration-300"
              >
                <ArrowLeft size={20} className="mr-2" />
                {returnUrl ? 'Volver' : 'Inicio'}
              </button>
              <div>
                <h1 className="text-universal-title font-bold">{playlist?.playlistTitle || `Volumen ${volumeNumber}`}</h1>
                <p className="text-purple-200 text-universal-caption">
                  {playlist?.items?.length || 0} videos disponibles
                  {playlist?.items && (
                    <span className="ml-2">
                      • {playlist.items.filter(item => item.is_watched).length} vistos
                    </span>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={toggleDarkMode}
              className={`p-3 rounded-full transition-all duration-300 ${
                darkMode 
                  ? 'bg-purple-700 hover:bg-purple-600' 
                  : 'bg-purple-700 hover:bg-purple-800'
              }`}
              aria-label="Toggle dark mode"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Chapter Search Section */}
        <div className="mb-8">
          <div className={`rounded-2xl p-6 shadow-lg border ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className="flex items-center mb-4">
              <div className={`p-2 rounded-lg mr-3 ${
                darkMode ? 'bg-purple-600' : 'bg-purple-100'
              }`}>
                <svg className={`w-5 h-5 ${darkMode ? 'text-white' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className={`text-lg font-semibold ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Buscar Capítulo en este Volumen
              </h3>
            </div>
            
            <div className="flex flex-row gap-3 items-center">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  placeholder="Índice (ej: 5)"
                  disabled={loading || !playlist || !playlist.items || !Array.isArray(playlist.items)}
                  className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300 ${
                    loading || !playlist || !playlist.items || !Array.isArray(playlist.items)
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  } ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-gray-50 border-gray-300 text-gray-800 placeholder-gray-500'
                  }`}
                />
              </div>
              <button
                onClick={handleChapterSearch}
                disabled={loading || !playlist || !playlist.items || !Array.isArray(playlist.items)}
                className={`px-4 py-3 rounded-xl font-medium transition-all duration-300 hover:transform hover:scale-105 whitespace-nowrap ${
                  loading || !playlist || !playlist.items || !Array.isArray(playlist.items)
                    ? 'opacity-50 cursor-not-allowed bg-gray-400'
                    : darkMode
                      ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg hover:shadow-xl'
                      : 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {loading ? 'Cargando...' : 'Buscar'}
              </button>
            </div>
            
            {/* Search Error/Success Message */}
            {searchError && (
              <div className="mt-4 p-3 rounded-lg bg-red-50 border-l-4 border-red-500 animate-in slide-in-from-top-2 duration-300">
                <p className={`text-sm font-medium ${
                  searchError === "No Disponible" || searchError.includes("no disponible")
                    ? 'text-red-600' 
                    : 'text-orange-600'
                }`}>
                  {searchError}
                </p>
              </div>
            )}
            
            {/* Quick Instructions */}
            <div className={`mt-3 text-xs ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <p>💡 <strong>Tip:</strong> Solo ingresa el número del capítulo que buscas (ej: 5 para el capítulo 5)</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video Player Section */}
          <div className="lg:col-span-2">
            <div className={`rounded-lg shadow-lg overflow-hidden ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              {selectedVideo ? (
                selectedVideo.videoId && selectedVideo.videoId.trim() !== "" ? (
                  <div className="aspect-video">
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${selectedVideo.videoId}`}
                      title={selectedVideo.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="aspect-video flex items-center justify-center bg-gray-300">
                    <div className="text-center">
                      <div className={`mx-auto mb-4 p-4 rounded-full ${
                        darkMode ? 'bg-gray-600 text-gray-400' : 'bg-gray-400 text-gray-600'
                      }`}>
                        <Play size={48} className="opacity-50" />
                      </div>
                      <p className={`text-lg font-medium mb-2 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Video no disponible
                      </p>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Este video aún no está disponible.
                      </p>
                    </div>
                  </div>
                )
              ) : (
                <div className="aspect-video flex items-center justify-center bg-gray-200">
                  <div className="text-center">
                    <Play size={48} className="mx-auto mb-4 text-gray-400" />
                    <p className="text-gray-600">Selecciona un video para reproducir</p>
                  </div>
                </div>
              )}
              
              {selectedVideo && (
                <div className="p-6">
                  {/* Action Buttons Row - Right Aligned */}
                  <div className="flex justify-end items-center mb-4">
                    <div className="flex items-center gap-3">
                      {isVideoAvailable(selectedVideo) ? (
                        <button
                          onClick={() => handleToggleWatched(selectedVideo)}
                          disabled={watchingVideo === selectedVideo.videoId}
                          className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                            selectedVideo.is_watched
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                          } ${watchingVideo === selectedVideo.videoId ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {watchingVideo === selectedVideo.videoId ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                          ) : selectedVideo.is_watched ? (
                            <CheckCircle size={16} className="mr-2" />
                          ) : (
                            <Circle size={16} className="mr-2" />
                          )}
                          {selectedVideo.is_watched ? "Marcar como no visto" : "Marcar como visto"}
                        </button>
                      ) : (
                        <a
                          href="https://donorbox.org/divinavoluntadapp"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 cursor-pointer ${
                            darkMode 
                              ? 'bg-yellow-500 hover:bg-yellow-400 text-black shadow-lg hover:shadow-xl' 
                              : 'bg-yellow-600 hover:bg-yellow-500 text-white shadow-lg hover:shadow-xl'
                          }`}
                        >
                          <svg 
                            className="w-4 h-4 mr-2" 
                            fill="currentColor" 
                            viewBox="0 0 20 20" 
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                          </svg>
                          Dona aquí
                        </a>
                      )}
                      
                      <button
                        onClick={handleShareChapter}
                        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:transform hover:scale-105 ${
                          darkMode 
                            ? 'bg-blue-600 text-white hover:bg-blue-700' 
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        } shadow-md hover:shadow-lg relative group`}
                        title={
                          shareCapabilities.webShare 
                            ? 'Compartir usando el menú nativo del dispositivo'
                            : shareCapabilities.clipboard
                            ? 'Copiar enlace y miniatura al portapapeles' 
                            : 'Copiar enlace para compartir'
                        }
                      >
                        <Share2 size={16} className="mr-2" />
                        {shareCapabilities.webShare ? 'Compartir' : 'Copiar Enlace'}
                        
                        {/* Capability indicator (small dot) */}
                        <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${
                          shareCapabilities.webShare 
                            ? 'bg-green-400' 
                            : 'bg-yellow-400'
                        } opacity-75`} />
                        
                        {/* Tooltip on hover */}
                        <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                          {shareCapabilities.browserName} - {shareCapabilities.webShare ? 'Compartir nativo' : 'Copia avanzada'}
                        </div>
                      </button>
                    </div>
                  </div>
                  
                  {/* Title Row - Full Width */}
                  <div className="mb-4">
                    <h2 className="text-universal-subtitle font-bold text-left w-full">{selectedVideo.title}</h2>
                  </div>
                  
                  {/* Share Success Message */}
                  {shareSuccess && (
                    <div className="mb-4 p-3 rounded-lg bg-green-50 border-l-4 border-green-500 animate-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <p className="text-green-800 text-sm font-medium">
                            {shareCapabilities.webShare 
                              ? '¡Capítulo e imagen compartidos exitosamente!'
                              : '¡Enlace y URL de miniatura copiados!'
                            }
                          </p>
                          {!shareCapabilities.webShare && (
                            <p className="text-green-700 text-xs mt-1">
                              Para mejores resultados en WhatsApp: envía el enlace y también la imagen de la miniatura por separado
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Share Error Message */}
                  {shareError && (
                    <div className="mb-4 p-3 rounded-lg bg-orange-50 border-l-4 border-orange-500 animate-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-orange-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p className="text-orange-800 text-sm font-medium">{shareError}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Navigation Buttons */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={goToPreviousVideo}
                      disabled={selectedVideoIndex <= 0}
                      className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                        selectedVideoIndex <= 0
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : darkMode 
                            ? 'bg-gray-700 text-white hover:bg-gray-600' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      <ChevronLeft size={16} className="mr-2" />
                      Anterior
                    </button>
                    
                    <span className={`text-universal-caption px-4 py-2 rounded-lg ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedVideoIndex + 1} de {playlist?.items?.length || 0}
                    </span>
                    
                    <button
                      onClick={goToNextVideo}
                      disabled={selectedVideoIndex >= (playlist?.items?.length || 0) - 1}
                      className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                        selectedVideoIndex >= (playlist?.items?.length || 0) - 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : darkMode 
                            ? 'bg-gray-700 text-white hover:bg-gray-600' 
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Siguiente
                      <ChevronRight size={16} className="ml-2" />
                    </button>
                  </div>
                  
                  {/* Content Section - Responsive Layout */}
                  {(mainFileContent || mainFileLoading || mainFileError) && (
                    <>
                      {/* Desktop Layout - Stacked vertically */}
                      <div className="hidden md:block mt-6">
                        {/* Main Content - Full width */}
                        <div className={`p-6 rounded-lg ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          {mainFileLoading && (
                            <div className="text-center py-4">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                              <p className="mt-2 text-universal-caption text-gray-500">Cargando contenido...</p>
                            </div>
                          )}
                          
                          {mainFileError && (
                            <div className="text-center py-4">
                              <p className="text-red-500 text-universal-caption">{mainFileError}</p>
                            </div>
                          )}
                          
                          {mainFileContent && !mainFileLoading && (
                            <div className="space-y-4 text-left">
                              {/* Date */}
                              <div className={`text-universal-reading text-left ${
                                darkMode ? 'text-gray-300' : 'text-gray-600'
                              }`}>
                                <strong>Fecha:</strong> {mainFileContent.date}
                              </div>
                              
                              {/* Description */}
                              <div className={`text-universal-reading text-left ${
                                darkMode ? 'text-gray-300' : 'text-gray-600'
                              }`}>
                                <strong>Descripción:</strong> {mainFileContent.description}
                              </div>
                              
                              {/* Dialogue */}
                              <div className="space-y-3 text-left">
                                <div className="space-y-3">
                                  {mainFileContent.dialogue.map((entry, index) => (
                                    <div key={index} className={`p-3 rounded-lg text-left ${
                                      darkMode ? 'bg-gray-800' : 'bg-white'
                                    } border-l-4 ${
                                      entry.speaker === 'Jesus' ? 'border-blue-500' : 
                                      entry.speaker === 'Luisa' ? 'border-pink-500' : 
                                      'border-gray-500'
                                    }`}>
                                      <div className={`text-universal-caption font-medium mb-1 text-left ${
                                        entry.speaker === 'Jesus' ? 'text-blue-600' : 
                                        entry.speaker === 'Luisa' ? 'text-pink-600' : 
                                        darkMode ? 'text-gray-300' : 'text-gray-700'
                                      }`}>
                                        {entry.speaker}:
                                      </div>
                                      <div className={`text-universal-reading text-left ${
                                        darkMode ? 'text-gray-200' : 'text-gray-800'
                                      }`}>
                                        <ReactMarkdown
                                          allowedElements={['p', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'br']}
                                          urlTransform={(uri) => {
                                            if (uri.startsWith('http://') || uri.startsWith('https://') || uri.startsWith('/')) {
                                              return uri;
                                            }
                                            return null;
                                          }}
                                          components={{
                                            a: ({ href, children, ...props }) => {
                                              const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
                                              return (
                                                <a
                                                  href={href}
                                                  target={isExternal ? '_blank' : '_self'}
                                                  rel={isExternal ? 'noopener noreferrer' : undefined}
                                                  className={`underline hover:no-underline ${
                                                    darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                                                  }`}
                                                  {...props}
                                                >
                                                  {children}
                                                </a>
                                              );
                                            }
                                          }}
                                        >
                                          {entry.text}
                                        </ReactMarkdown>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                
                                {/* Bottom Action Buttons */}
                                <div className="pt-4 border-t border-gray-300">
                                  <div className="flex items-center justify-between mb-4">
                                    {isVideoAvailable(selectedVideo) ? (
                                      <button
                                        onClick={() => handleToggleWatched(selectedVideo)}
                                        disabled={watchingVideo === selectedVideo.videoId}
                                        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                          selectedVideo.is_watched
                                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                        } ${watchingVideo === selectedVideo.videoId ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      >
                                        {watchingVideo === selectedVideo.videoId ? (
                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                                        ) : selectedVideo.is_watched ? (
                                          <CheckCircle size={16} className="mr-2" />
                                        ) : (
                                          <Circle size={16} className="mr-2" />
                                        )}
                                        {selectedVideo.is_watched ? "Marcar como no visto" : "Marcar como visto"}
                                      </button>
                                    ) : (
                                      <a
                                        href="https://donorbox.org/divinavoluntadapp"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 cursor-pointer ${
                                          darkMode 
                                            ? 'bg-yellow-500 hover:bg-yellow-400 text-black shadow-lg hover:shadow-xl' 
                                            : 'bg-yellow-600 hover:bg-yellow-500 text-white shadow-lg hover:shadow-xl'
                                        }`}
                                      >
                                        <svg 
                                          className="w-4 h-4 mr-2" 
                                          fill="currentColor" 
                                          viewBox="0 0 20 20" 
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                        </svg>
                                        Dona aquí
                                      </a>
                                    )}
                                    
                                    <span className={`text-universal-caption px-4 py-2 rounded-lg ${
                                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {selectedVideoIndex + 1} de {playlist?.items?.length || 0}
                                    </span>
                                  </div>
                                  
                                  {/* Navigation Buttons */}
                                  <div className="flex items-center justify-between">
                                    <button
                                      onClick={goToPreviousVideo}
                                      disabled={selectedVideoIndex <= 0}
                                      className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                        selectedVideoIndex <= 0
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                          : darkMode 
                                            ? 'bg-gray-700 text-white hover:bg-gray-600' 
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                      }`}
                                    >
                                      <ChevronLeft size={16} className="mr-2" />
                                      Anterior
                                    </button>
                                    
                                    <button
                                      onClick={goToNextVideo}
                                      disabled={selectedVideoIndex >= (playlist?.items?.length || 0) - 1}
                                      className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                        selectedVideoIndex >= (playlist?.items?.length || 0) - 1
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                          : darkMode 
                                            ? 'bg-gray-700 text-white hover:bg-gray-600' 
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                      }`}
                                    >
                                      Siguiente
                                      <ChevronRight size={16} className="ml-2" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Optional Content Below - Desktop */}
                        {optionalFileContent && optionalFileContent.dialogue_summary && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            {/* Summary Title */}
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              Resumen
                            </h3>
                            <div className="space-y-3 text-left">
                              {optionalFileContent.dialogue_summary.map((entry, index) => {
                                const normalizedText = entry.text || entry.summary;
                                return (
                                  <div key={index} className={`p-3 rounded-lg text-left ${
                                    darkMode ? 'bg-gray-800' : 'bg-white'
                                  } border-l-4 ${
                                    entry.speaker === 'Jesus' ? 'border-blue-500' : 
                                    entry.speaker === 'Luisa' ? 'border-pink-500' : 
                                    'border-gray-500'
                                  }`}>
                                    <div className={`text-universal-caption font-medium mb-1 text-left ${
                                      entry.speaker === 'Jesus' ? 'text-blue-600' : 
                                      entry.speaker === 'Luisa' ? 'text-pink-600' : 
                                      darkMode ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                      {entry.speaker}:
                                    </div>
                                    <div className={`text-universal-reading text-left ${
                                      darkMode ? 'text-gray-200' : 'text-gray-800'
                                    }`}>
                                      <ReactMarkdown
                                        allowedElements={['p', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'br']}
                                        urlTransform={(uri) => {
                                          if (uri.startsWith('http://') || uri.startsWith('https://') || uri.startsWith('/')) {
                                            return uri;
                                          }
                                          return null;
                                        }}
                                        components={{
                                          a: ({ href, children, ...props }) => {
                                            const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
                                            return (
                                              <a
                                                href={href}
                                                target={isExternal ? '_blank' : '_self'}
                                                rel={isExternal ? 'noopener noreferrer' : undefined}
                                                className={`underline hover:no-underline ${
                                                  darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                                                }`}
                                                {...props}
                                              >
                                                {children}
                                              </a>
                                            );
                                          }
                                        }}
                                      >
                                        {normalizedText}
                                      </ReactMarkdown>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Quiz Section - Desktop */}
                        {optionalFileContent && optionalFileContent.quiz && optionalFileContent.quiz.questions && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              ❓ Preguntas y Respuestas
                            </h3>
                            <div className="space-y-4">
                              {optionalFileContent.quiz.questions.map((question, index) => {
                                const answer = optionalFileContent.quiz.answers?.[index];
                                const isExpanded = expandedQuestions.includes(`desktop-${index}`);
                                
                                return (
                                  <div key={index} className={`border rounded-lg p-4 ${
                                    darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'
                                  }`}>
                                    <div className={`text-universal-reading font-medium mb-3 text-left ${
                                      darkMode ? 'text-gray-200' : 'text-gray-800'
                                    }`}>
                                      {index + 1}. {question}
                                    </div>
                                    
                                    <button
                                      onClick={() => toggleQuestion(`desktop-${index}`)}
                                      className={`flex items-center space-x-2 w-full text-left p-2 rounded-md transition-colors duration-200 ${
                                        darkMode 
                                          ? 'hover:bg-gray-700 text-purple-300' 
                                          : 'hover:bg-gray-100 text-purple-600'
                                      }`}
                                      aria-expanded={isExpanded}
                                      aria-controls={`answer-${index}`}
                                    >
                                      <span className="text-lg select-none">
                                        {isExpanded ? '▼' : '▶'}
                                      </span>
                                      <span className="text-universal-caption">
                                        {isExpanded ? 'Ocultar respuesta' : 'Mostrar respuesta'}
                                      </span>
                                    </button>
                                    
                                    {isExpanded && answer && (
                                      <div
                                        id={`answer-${index}`}
                                        className={`mt-3 p-3 rounded-md text-universal-reading text-left ${
                                          darkMode ? 'bg-gray-700 text-gray-200' : 'bg-purple-50 text-gray-800'
                                        } border-l-4 border-purple-500 animate-in slide-in-from-top-2 duration-300`}
                                      >
                                        {answer}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* New Testament References Section - Desktop */}
                        {optionalFileContent && optionalFileContent.new_testament && optionalFileContent.new_testament.length > 0 && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              📖 Referencias Bíblicas
                            </h3>
                            <div className="space-y-4">
                              {optionalFileContent.new_testament.map((reference, index) => (
                                <div key={index} className={`border-l-4 border-purple-500 rounded-lg p-4 transition-all duration-300 shadow-sm hover:shadow-md ${
                                  darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                                }`}>
                                  <div className="flex items-start space-x-3">
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                                      darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-600'
                                    }`}>
                                      <span className="text-sm">📖</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className={`text-universal-reading font-semibold mb-2 ${
                                        darkMode ? 'text-purple-300' : 'text-purple-700'
                                      }`}>
                                        {reference.reference}
                                      </div>
                                      {reference.description && (
                                        <div className={`text-universal-reading text-left leading-relaxed mb-2 italic ${
                                          darkMode ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                          {reference.description}
                                        </div>
                                      )}
                                      {reference.citation && (
                                        <button
                                          onClick={() => openBibleDialog(reference.reference)}
                                          className={`text-universal-caption text-left underline cursor-pointer transition-colors duration-200 hover:opacity-80 ${
                                            darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                                          }`}
                                        >
                                          Consultar cita bíblica
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Homework Section - Desktop */}
                        {optionalFileContent && optionalFileContent.homework && optionalFileContent.homework.length > 0 && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              ✅ Ejercicios Espirituales
                            </h3>
                            <div className="space-y-3">
                              {optionalFileContent.homework.map((task, index) => (
                                <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg transition-all duration-300 hover:shadow-sm ${
                                  darkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'
                                } border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm ${
                                    darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'
                                  }`}>
                                    {index + 1}
                                  </div>
                                  <div className={`flex-1 text-universal-reading text-left ${
                                    darkMode ? 'text-gray-200' : 'text-gray-800'
                                  }`}>
                                    {task}
                                  </div>
                                  <div className={`flex-shrink-0 ${
                                    darkMode ? 'text-green-400' : 'text-green-600'
                                  }`}>
                                    <span className="text-lg">✅</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Bulletpoints Section - Desktop */}
                        {optionalFileContent && optionalFileContent.bulletpoints && optionalFileContent.bulletpoints.length > 0 && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              🔹 Puntos Clave
                            </h3>
                            <div className="space-y-3">
                              {optionalFileContent.bulletpoints.map((point, index) => (
                                <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg transition-all duration-300 hover:shadow-sm ${
                                  darkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'
                                } border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                  <div className={`flex-shrink-0 w-3 h-3 rounded-full mt-2 ${
                                    darkMode ? 'bg-blue-400' : 'bg-blue-500'
                                  }`}>
                                  </div>
                                  <div className={`flex-1 text-universal-reading text-left ${
                                    darkMode ? 'text-gray-200' : 'text-gray-800'
                                  }`}>
                                    {point}
                                  </div>
                                  <div className={`flex-shrink-0 ${
                                    darkMode ? 'text-blue-400' : 'text-blue-500'
                                  }`}>
                                    <span className="text-lg">💡</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* User Comments Section - Desktop */}
                        {optionalFileContent && optionalFileContent.user_comments && optionalFileContent.user_comments.length > 0 && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              💬 Comentarios Públicos
                            </h3>
                            <div className="space-y-4">
                              {optionalFileContent.user_comments.map((userComment, index) => (
                                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                                  darkMode 
                                    ? 'bg-gray-800 border-l-blue-400 border border-gray-600' 
                                    : 'bg-white border-l-blue-500 border border-gray-200'
                                } transition-all duration-300 hover:shadow-sm`}>
                                  <div className={`font-medium text-sm mb-2 ${
                                    darkMode ? 'text-blue-400' : 'text-blue-600'
                                  }`}>
                                    👤 {userComment.user}
                                  </div>
                                  <div className={`text-universal-reading ${
                                    darkMode ? 'text-gray-200' : 'text-gray-800'
                                  }`}>
                                    <ReactMarkdown
                                      allowedElements={['p', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'br']}
                                      urlTransform={(uri) => {
                                        // Only allow http/https links and internal routes
                                        if (uri.startsWith('http://') || uri.startsWith('https://') || uri.startsWith('/')) {
                                          return uri;
                                        }
                                        return null;
                                      }}
                                      components={{
                                        // Custom link component to handle target behavior
                                        a: ({ href, children, ...props }) => {
                                          const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
                                          return (
                                            <a
                                              href={href}
                                              target={isExternal ? '_blank' : '_self'}
                                              rel={isExternal ? 'noopener noreferrer' : undefined}
                                              className={`underline hover:no-underline ${
                                                darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                                              }`}
                                              {...props}
                                            >
                                              {children}
                                            </a>
                                          );
                                        }
                                      }}
                                    >
                                      {userComment.comment}
                                    </ReactMarkdown>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Topics Section - Desktop */}
                        {optionalFileContent && optionalFileContent.topics && optionalFileContent.topics.length > 0 && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              🏷️ Temas Principales
                            </h3>
                            <div className="flex flex-wrap gap-3">
                              {optionalFileContent.topics.map((topic, index) => {
                                // Color rotation for pills
                                const colors = [
                                  { bg: darkMode ? 'bg-purple-900' : 'bg-purple-100', text: darkMode ? 'text-purple-300' : 'text-purple-700', border: 'border-purple-500' },
                                  { bg: darkMode ? 'bg-blue-900' : 'bg-blue-100', text: darkMode ? 'text-blue-300' : 'text-blue-700', border: 'border-blue-500' },
                                  { bg: darkMode ? 'bg-green-900' : 'bg-green-100', text: darkMode ? 'text-green-300' : 'text-green-700', border: 'border-green-500' },
                                  { bg: darkMode ? 'bg-red-900' : 'bg-red-100', text: darkMode ? 'text-red-300' : 'text-red-700', border: 'border-red-500' },
                                  { bg: darkMode ? 'bg-yellow-900' : 'bg-yellow-100', text: darkMode ? 'text-yellow-300' : 'text-yellow-700', border: 'border-yellow-500' },
                                  { bg: darkMode ? 'bg-indigo-900' : 'bg-indigo-100', text: darkMode ? 'text-indigo-300' : 'text-indigo-700', border: 'border-indigo-500' },
                                ];
                                const colorSet = colors[index % colors.length];
                                
                                return (
                                  <div
                                    key={index}
                                    onClick={() => handleTopicClick(topic)}
                                    className={`inline-flex items-center px-4 py-2 rounded-full border-2 transition-all duration-300 hover:shadow-lg hover:scale-105 cursor-pointer ${
                                      colorSet.bg
                                    } ${colorSet.text} ${colorSet.border}`}
                                  >
                                    <span className="text-universal-caption font-medium">
                                      {topic}
                                    </span>
                                    <span className="ml-2 text-sm">🏷️</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Personal Notes Section - Desktop */}
                        {selectedVideo && isAuthenticated && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              📝 Notas Personales
                            </h3>
                            
                            {noteLoading ? (
                              <div className="text-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                                <p className="mt-2 text-universal-caption text-gray-500">Cargando nota...</p>
                              </div>
                            ) : (
                              <div className="space-y-4">
                                <textarea
                                  value={noteText}
                                  onChange={(e) => setNoteText(e.target.value)}
                                  placeholder="Escribe tus notas personales sobre este capítulo..."
                                  className={`w-full h-32 p-4 rounded-lg border-2 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300 ${
                                    darkMode 
                                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                                      : 'bg-white border-gray-300 text-gray-800 placeholder-gray-500'
                                  }`}
                                />
                                
                                <div className="flex items-center justify-between">
                                  <div className="text-universal-caption text-gray-500">
                                    {noteText.length}/2000 caracteres
                                  </div>
                                  
                                  <div className="flex items-center space-x-3">
                                    {noteError && (
                                      <span className="text-red-500 text-universal-caption">{noteError}</span>
                                    )}
                                    
                                    {noteSuccess && (
                                      <span className="text-green-500 text-universal-caption flex items-center">
                                        <Check size={16} className="mr-1" />
                                        Nota guardada
                                      </span>
                                    )}
                                    
                                    <button
                                      onClick={saveUserNote}
                                      disabled={noteSaving || noteText.length > 2000}
                                      className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                        noteSaving || noteText.length > 2000
                                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                          : darkMode
                                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                                            : 'bg-purple-600 text-white hover:bg-purple-700'
                                      }`}
                                    >
                                      {noteSaving ? (
                                        <div className="flex items-center">
                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                          Guardando...
                                        </div>
                                      ) : (
                                        'Guardar Nota'
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Bottom Navigation Buttons - Desktop */}
                        {selectedVideo && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <div className="flex items-center justify-between">
                              <button
                                onClick={goToPreviousVideo}
                                disabled={selectedVideoIndex <= 0}
                                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                  selectedVideoIndex <= 0
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : darkMode 
                                      ? 'bg-gray-700 text-white hover:bg-gray-600' 
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                <ChevronLeft size={16} className="mr-2" />
                                Anterior
                              </button>
                              
                              <span className={`text-universal-caption px-4 py-2 rounded-lg ${
                                darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {selectedVideoIndex + 1} de {playlist?.items?.length || 0}
                              </span>
                              
                              <button
                                onClick={goToNextVideo}
                                disabled={selectedVideoIndex >= (playlist?.items?.length || 0) - 1}
                                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                  selectedVideoIndex >= (playlist?.items?.length || 0) - 1
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : darkMode 
                                      ? 'bg-gray-700 text-white hover:bg-gray-600' 
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                Siguiente
                                <ChevronRight size={16} className="ml-2" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Mobile Layout - Stacked */}
                      <div className="md:hidden mt-6">
                        {/* Main Content */}
                        <div className={`p-6 rounded-lg ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-50'
                        }`}>
                          {mainFileLoading && (
                            <div className="text-center py-4">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                              <p className="mt-2 text-universal-caption text-gray-500">Cargando contenido...</p>
                            </div>
                          )}
                          
                          {mainFileError && (
                            <div className="text-center py-4">
                              <p className="text-red-500 text-universal-caption">{mainFileError}</p>
                            </div>
                          )}
                          
                          {mainFileContent && !mainFileLoading && (
                            <div className="space-y-4 text-left">
                              {/* Date */}
                              <div className={`text-universal-reading text-left ${
                                darkMode ? 'text-gray-300' : 'text-gray-600'
                              }`}>
                                <strong>Fecha:</strong> {mainFileContent.date}
                              </div>
                              
                              {/* Description */}
                              <div className={`text-universal-reading text-left ${
                                darkMode ? 'text-gray-300' : 'text-gray-600'
                              }`}>
                                <strong>Descripción:</strong> {mainFileContent.description}
                              </div>
                              
                              {/* Dialogue */}
                              <div className="space-y-3 text-left">
                                <div className="space-y-3">
                                  {mainFileContent.dialogue.map((entry, index) => (
                                    <div key={index} className={`p-3 rounded-lg text-left ${
                                      darkMode ? 'bg-gray-800' : 'bg-white'
                                    } border-l-4 ${
                                      entry.speaker === 'Jesus' ? 'border-blue-500' : 
                                      entry.speaker === 'Luisa' ? 'border-pink-500' : 
                                      'border-gray-500'
                                    }`}>
                                      <div className={`text-universal-caption font-medium mb-1 text-left ${
                                        entry.speaker === 'Jesus' ? 'text-blue-600' : 
                                        entry.speaker === 'Luisa' ? 'text-pink-600' : 
                                        darkMode ? 'text-gray-300' : 'text-gray-700'
                                      }`}>
                                        {entry.speaker}:
                                      </div>
                                      <div className={`text-universal-reading text-left ${
                                        darkMode ? 'text-gray-200' : 'text-gray-800'
                                      }`}>
                                        <ReactMarkdown
                                          allowedElements={['p', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'br']}
                                          urlTransform={(uri) => {
                                            if (uri.startsWith('http://') || uri.startsWith('https://') || uri.startsWith('/')) {
                                              return uri;
                                            }
                                            return null;
                                          }}
                                          components={{
                                            a: ({ href, children, ...props }) => {
                                              const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
                                              return (
                                                <a
                                                  href={href}
                                                  target={isExternal ? '_blank' : '_self'}
                                                  rel={isExternal ? 'noopener noreferrer' : undefined}
                                                  className={`underline hover:no-underline ${
                                                    darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                                                  }`}
                                                  {...props}
                                                >
                                                  {children}
                                                </a>
                                              );
                                            }
                                          }}
                                        >
                                          {entry.text}
                                        </ReactMarkdown>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                
                                {/* Bottom Action Buttons */}
                                <div className="pt-4 border-t border-gray-300">
                                  <div className="flex items-center justify-between mb-4">
                                    {isVideoAvailable(selectedVideo) ? (
                                      <button
                                        onClick={() => handleToggleWatched(selectedVideo)}
                                        disabled={watchingVideo === selectedVideo.videoId}
                                        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                          selectedVideo.is_watched
                                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                        } ${watchingVideo === selectedVideo.videoId ? 'opacity-50 cursor-not-allowed' : ''}`}
                                      >
                                        {watchingVideo === selectedVideo.videoId ? (
                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                                        ) : selectedVideo.is_watched ? (
                                          <CheckCircle size={16} className="mr-2" />
                                        ) : (
                                          <Circle size={16} className="mr-2" />
                                        )}
                                        {selectedVideo.is_watched ? "Marcar como no visto" : "Marcar como visto"}
                                      </button>
                                    ) : (
                                      <a
                                        href="https://donorbox.org/divinavoluntadapp"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`inline-flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 cursor-pointer ${
                                          darkMode 
                                            ? 'bg-yellow-500 hover:bg-yellow-400 text-black shadow-lg hover:shadow-xl' 
                                            : 'bg-yellow-600 hover:bg-yellow-500 text-white shadow-lg hover:shadow-xl'
                                        }`}
                                      >
                                        <svg 
                                          className="w-4 h-4 mr-2" 
                                          fill="currentColor" 
                                          viewBox="0 0 20 20" 
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                        </svg>
                                        Dona aquí
                                      </a>
                                    )}
                                    
                                    <span className={`text-universal-caption px-4 py-2 rounded-lg ${
                                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {selectedVideoIndex + 1} de {playlist?.items?.length || 0}
                                    </span>
                                  </div>
                                  
                                  {/* Navigation Buttons */}
                                  <div className="flex items-center justify-between">
                                    <button
                                      onClick={goToPreviousVideo}
                                      disabled={selectedVideoIndex <= 0}
                                      className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                        selectedVideoIndex <= 0
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                          : darkMode 
                                            ? 'bg-gray-700 text-white hover:bg-gray-600' 
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                      }`}
                                    >
                                      <ChevronLeft size={16} className="mr-2" />
                                      Anterior
                                    </button>
                                    
                                    <button
                                      onClick={goToNextVideo}
                                      disabled={selectedVideoIndex >= (playlist?.items?.length || 0) - 1}
                                      className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                        selectedVideoIndex >= (playlist?.items?.length || 0) - 1
                                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                          : darkMode 
                                            ? 'bg-gray-700 text-white hover:bg-gray-600' 
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                      }`}
                                    >
                                      Siguiente
                                      <ChevronRight size={16} className="ml-2" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Optional Content Below Navigation - Mobile Only */}
                        {optionalFileContent && optionalFileContent.dialogue_summary && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            {/* Summary Title */}
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              Resumen
                            </h3>
                            <div className="space-y-3 text-left">
                              {optionalFileContent.dialogue_summary.map((entry, index) => {
                                const normalizedText = entry.text || entry.summary;
                                return (
                                  <div key={index} className={`p-3 rounded-lg text-left ${
                                    darkMode ? 'bg-gray-800' : 'bg-white'
                                  } border-l-4 ${
                                    entry.speaker === 'Jesus' ? 'border-blue-500' : 
                                    entry.speaker === 'Luisa' ? 'border-pink-500' : 
                                    'border-gray-500'
                                  }`}>
                                    <div className={`text-universal-caption font-medium mb-1 text-left ${
                                      entry.speaker === 'Jesus' ? 'text-blue-600' : 
                                      entry.speaker === 'Luisa' ? 'text-pink-600' : 
                                      darkMode ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                      {entry.speaker}:
                                    </div>
                                    <div className={`text-universal-reading text-left ${
                                      darkMode ? 'text-gray-200' : 'text-gray-800'
                                    }`}>
                                      <ReactMarkdown
                                        allowedElements={['p', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'br']}
                                        urlTransform={(uri) => {
                                          if (uri.startsWith('http://') || uri.startsWith('https://') || uri.startsWith('/')) {
                                            return uri;
                                          }
                                          return null;
                                        }}
                                        components={{
                                          a: ({ href, children, ...props }) => {
                                            const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
                                            return (
                                              <a
                                                href={href}
                                                target={isExternal ? '_blank' : '_self'}
                                                rel={isExternal ? 'noopener noreferrer' : undefined}
                                                className={`underline hover:no-underline ${
                                                  darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                                                }`}
                                                {...props}
                                              >
                                                {children}
                                              </a>
                                            );
                                          }
                                        }}
                                      >
                                        {normalizedText}
                                      </ReactMarkdown>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Quiz Section - Mobile */}
                        {optionalFileContent && optionalFileContent.quiz && optionalFileContent.quiz.questions && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              ❓ Preguntas y Respuestas
                            </h3>
                            <div className="space-y-4">
                              {optionalFileContent.quiz.questions.map((question, index) => {
                                const answer = optionalFileContent.quiz.answers?.[index];
                                const isExpanded = expandedQuestions.includes(`mobile-${index}`);
                                
                                return (
                                  <div key={index} className={`border rounded-lg p-4 ${
                                    darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'
                                  }`}>
                                    <div className={`text-universal-reading font-medium mb-3 text-left ${
                                      darkMode ? 'text-gray-200' : 'text-gray-800'
                                    }`}>
                                      {index + 1}. {question}
                                    </div>
                                    
                                    <button
                                      onClick={() => toggleQuestion(`mobile-${index}`)}
                                      className={`flex items-center space-x-2 w-full text-left p-2 rounded-md transition-colors duration-200 ${
                                        darkMode 
                                          ? 'hover:bg-gray-700 text-purple-300' 
                                          : 'hover:bg-gray-100 text-purple-600'
                                      }`}
                                      aria-expanded={isExpanded}
                                      aria-controls={`mobile-answer-${index}`}
                                    >
                                      <span className="text-lg select-none">
                                        {isExpanded ? '▼' : '▶'}
                                      </span>
                                      <span className="text-universal-caption">
                                        {isExpanded ? 'Ocultar respuesta' : 'Mostrar respuesta'}
                                      </span>
                                    </button>
                                    
                                    {isExpanded && answer && (
                                      <div
                                        id={`mobile-answer-${index}`}
                                        className={`mt-3 p-3 rounded-md text-universal-reading text-left ${
                                          darkMode ? 'bg-gray-700 text-gray-200' : 'bg-purple-50 text-gray-800'
                                        } border-l-4 border-purple-500 animate-in slide-in-from-top-2 duration-300`}
                                      >
                                        {answer}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* New Testament References Section - Mobile */}
                        {optionalFileContent && optionalFileContent.new_testament && optionalFileContent.new_testament.length > 0 && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              📖 Referencias Bíblicas
                            </h3>
                            <div className="space-y-4">
                              {optionalFileContent.new_testament.map((reference, index) => (
                                <div key={index} className={`border-l-4 border-purple-500 rounded-lg p-4 transition-all duration-300 shadow-sm ${
                                  darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                                }`}>
                                  <div className="flex items-start space-x-3">
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                                      darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-600'
                                    }`}>
                                      <span className="text-sm">📖</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className={`text-universal-reading font-semibold mb-2 ${
                                        darkMode ? 'text-purple-300' : 'text-purple-700'
                                      }`}>
                                        {reference.reference}
                                      </div>
                                      {reference.description && (
                                        <div className={`text-universal-reading text-left leading-relaxed mb-2 italic ${
                                          darkMode ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                          {reference.description}
                                        </div>
                                      )}
                                      {reference.citation && (
                                        <button
                                          onClick={() => openBibleDialog(reference.reference)}
                                          className={`text-universal-caption text-left underline cursor-pointer transition-colors duration-200 hover:opacity-80 ${
                                            darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                                          }`}
                                        >
                                          Consultar cita bíblica
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Homework Section - Mobile */}
                        {optionalFileContent && optionalFileContent.homework && optionalFileContent.homework.length > 0 && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              ✅ Ejercicios Espirituales
                            </h3>
                            <div className="space-y-3">
                              {optionalFileContent.homework.map((task, index) => (
                                <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg transition-all duration-300 hover:shadow-sm ${
                                  darkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'
                                } border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm ${
                                    darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-700'
                                  }`}>
                                    {index + 1}
                                  </div>
                                  <div className={`flex-1 text-universal-reading text-left ${
                                    darkMode ? 'text-gray-200' : 'text-gray-800'
                                  }`}>
                                    {task}
                                  </div>
                                  <div className={`flex-shrink-0 ${
                                    darkMode ? 'text-green-400' : 'text-green-600'
                                  }`}>
                                    <span className="text-lg">✅</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Bulletpoints Section - Mobile */}
                        {optionalFileContent && optionalFileContent.bulletpoints && optionalFileContent.bulletpoints.length > 0 && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              🔹 Puntos Clave
                            </h3>
                            <div className="space-y-3">
                              {optionalFileContent.bulletpoints.map((point, index) => (
                                <div key={index} className={`flex items-start space-x-3 p-3 rounded-lg transition-all duration-300 hover:shadow-sm ${
                                  darkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-gray-50'
                                } border ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                  <div className={`flex-shrink-0 w-3 h-3 rounded-full mt-2 ${
                                    darkMode ? 'bg-blue-400' : 'bg-blue-500'
                                  }`}>
                                  </div>
                                  <div className={`flex-1 text-universal-reading text-left ${
                                    darkMode ? 'text-gray-200' : 'text-gray-800'
                                  }`}>
                                    {point}
                                  </div>
                                  <div className={`flex-shrink-0 ${
                                    darkMode ? 'text-blue-400' : 'text-blue-500'
                                  }`}>
                                    <span className="text-lg">💡</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* User Comments Section - Mobile */}
                        {optionalFileContent && optionalFileContent.user_comments && optionalFileContent.user_comments.length > 0 && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              💬 Comentarios Públicos
                            </h3>
                            <div className="space-y-4">
                              {optionalFileContent.user_comments.map((userComment, index) => (
                                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                                  darkMode 
                                    ? 'bg-gray-800 border-l-blue-400 border border-gray-600' 
                                    : 'bg-white border-l-blue-500 border border-gray-200'
                                } transition-all duration-300 hover:shadow-sm`}>
                                  <div className={`font-medium text-sm mb-2 ${
                                    darkMode ? 'text-blue-400' : 'text-blue-600'
                                  }`}>
                                    👤 {userComment.user}
                                  </div>
                                  <div className={`text-universal-reading ${
                                    darkMode ? 'text-gray-200' : 'text-gray-800'
                                  }`}>
                                    <ReactMarkdown
                                      allowedElements={['p', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'br']}
                                      urlTransform={(uri) => {
                                        // Only allow http/https links and internal routes
                                        if (uri.startsWith('http://') || uri.startsWith('https://') || uri.startsWith('/')) {
                                          return uri;
                                        }
                                        return null;
                                      }}
                                      components={{
                                        // Custom link component to handle target behavior
                                        a: ({ href, children, ...props }) => {
                                          const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
                                          return (
                                            <a
                                              href={href}
                                              target={isExternal ? '_blank' : '_self'}
                                              rel={isExternal ? 'noopener noreferrer' : undefined}
                                              className={`underline hover:no-underline ${
                                                darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                                              }`}
                                              {...props}
                                            >
                                              {children}
                                            </a>
                                          );
                                        }
                                      }}
                                    >
                                      {userComment.comment}
                                    </ReactMarkdown>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Topics Section - Mobile */}
                        {optionalFileContent && optionalFileContent.topics && optionalFileContent.topics.length > 0 && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              🏷️ Temas Principales
                            </h3>
                            <div className="flex flex-wrap gap-3">
                              {optionalFileContent.topics.map((topic, index) => {
                                // Color rotation for pills
                                const colors = [
                                  { bg: darkMode ? 'bg-purple-900' : 'bg-purple-100', text: darkMode ? 'text-purple-300' : 'text-purple-700', border: 'border-purple-500' },
                                  { bg: darkMode ? 'bg-blue-900' : 'bg-blue-100', text: darkMode ? 'text-blue-300' : 'text-blue-700', border: 'border-blue-500' },
                                  { bg: darkMode ? 'bg-green-900' : 'bg-green-100', text: darkMode ? 'text-green-300' : 'text-green-700', border: 'border-green-500' },
                                  { bg: darkMode ? 'bg-red-900' : 'bg-red-100', text: darkMode ? 'text-red-300' : 'text-red-700', border: 'border-red-500' },
                                  { bg: darkMode ? 'bg-yellow-900' : 'bg-yellow-100', text: darkMode ? 'text-yellow-300' : 'text-yellow-700', border: 'border-yellow-500' },
                                  { bg: darkMode ? 'bg-indigo-900' : 'bg-indigo-100', text: darkMode ? 'text-indigo-300' : 'text-indigo-700', border: 'border-indigo-500' },
                                ];
                                const colorSet = colors[index % colors.length];
                                
                                return (
                                  <div
                                    key={index}
                                    onClick={() => handleTopicClick(topic)}
                                    className={`inline-flex items-center px-4 py-2 rounded-full border-2 transition-all duration-300 hover:shadow-lg hover:scale-105 cursor-pointer ${
                                      colorSet.bg
                                    } ${colorSet.text} ${colorSet.border}`}
                                  >
                                    <span className="text-universal-caption font-medium">
                                      {topic}
                                    </span>
                                    <span className="ml-2 text-sm">🏷️</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Personal Notes Section - Mobile */}
                        {selectedVideo && isAuthenticated && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <h3 className={`text-universal-subtitle font-semibold mb-4 ${
                              darkMode ? 'text-gray-200' : 'text-gray-800'
                            }`}>
                              📝 Notas Personales
                            </h3>
                            
                            {noteLoading ? (
                              <div className="text-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                                <p className="mt-2 text-universal-caption text-gray-500">Cargando nota...</p>
                              </div>
                            ) : (
                              <div className="space-y-4">
                                <textarea
                                  value={noteText}
                                  onChange={(e) => setNoteText(e.target.value)}
                                  placeholder="Escribe tus notas personales sobre este capítulo..."
                                  className={`w-full h-32 p-4 rounded-lg border-2 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all duration-300 ${
                                    darkMode 
                                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                                      : 'bg-white border-gray-300 text-gray-800 placeholder-gray-500'
                                  }`}
                                />
                                
                                <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                                  <div className="text-universal-caption text-gray-500">
                                    {noteText.length}/2000 caracteres
                                  </div>
                                  
                                  <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-x-3 sm:space-y-0">
                                    {noteError && (
                                      <span className="text-red-500 text-universal-caption">{noteError}</span>
                                    )}
                                    
                                    {noteSuccess && (
                                      <span className="text-green-500 text-universal-caption flex items-center">
                                        <Check size={16} className="mr-1" />
                                        Nota guardada
                                      </span>
                                    )}
                                    
                                    <button
                                      onClick={saveUserNote}
                                      disabled={noteSaving || noteText.length > 2000}
                                      className={`w-full sm:w-auto px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                        noteSaving || noteText.length > 2000
                                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                          : darkMode
                                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                                            : 'bg-purple-600 text-white hover:bg-purple-700'
                                      }`}
                                    >
                                      {noteSaving ? (
                                        <div className="flex items-center justify-center">
                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                          Guardando...
                                        </div>
                                      ) : (
                                        'Guardar Nota'
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Bottom Navigation Buttons - Mobile */}
                        {selectedVideo && (
                          <div className={`mt-6 p-6 rounded-lg ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-50'
                          }`}>
                            <div className="flex items-center justify-between">
                              <button
                                onClick={goToPreviousVideo}
                                disabled={selectedVideoIndex <= 0}
                                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                  selectedVideoIndex <= 0
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : darkMode 
                                      ? 'bg-gray-700 text-white hover:bg-gray-600' 
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                <ChevronLeft size={16} className="mr-2" />
                                Anterior
                              </button>
                              
                              <span className={`text-universal-caption px-4 py-2 rounded-lg ${
                                darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {selectedVideoIndex + 1} de {playlist?.items?.length || 0}
                              </span>
                              
                              <button
                                onClick={goToNextVideo}
                                disabled={selectedVideoIndex >= (playlist?.items?.length || 0) - 1}
                                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                                  selectedVideoIndex >= (playlist?.items?.length || 0) - 1
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : darkMode 
                                      ? 'bg-gray-700 text-white hover:bg-gray-600' 
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                Siguiente
                                <ChevronRight size={16} className="ml-2" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Videos List */}
          <div className="lg:col-span-1">
            <div className={`rounded-lg shadow-lg overflow-hidden ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-universal-subtitle font-bold">Videos</h3>
                <p className="text-universal-caption text-gray-500 mt-1">
                  {playlist?.items?.filter(item => item.is_watched).length || 0} de {playlist?.items?.length || 0} vistos
                </p>
              </div>
              
              <div className="max-h-[70vh] overflow-y-auto">
                {playlist?.items?.map((video, index) => (
                  <div
                    key={video.videoId}
                    data-video-id={video.videoId}
                    className={`p-4 cursor-pointer transition-all duration-300 border-b border-gray-200 hover:bg-gray-50 ${
                      selectedVideo?.videoId === video.videoId
                        ? darkMode 
                          ? 'bg-purple-900 border-purple-700' 
                          : 'bg-purple-50 border-purple-200'
                        : darkMode 
                          ? 'hover:bg-gray-700' 
                          : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex space-x-3">
                      <div className="flex-shrink-0 relative">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-20 h-14 object-cover rounded"
                        />
                        {video.is_watched && (
                          <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1">
                            <CheckCircle size={12} className="text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 
                              className={`text-universal-caption font-medium leading-tight mb-1 cursor-pointer ${
                                selectedVideo?.videoId === video.videoId
                                  ? 'text-purple-600 font-semibold'
                                  : darkMode ? 'text-white' : 'text-gray-900'
                              }`}
                              onClick={() => handleVideoClick(video)}
                            >
                              {video.title}
                            </h4>
                            <p className={`text-xs ${
                              darkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              {video.channelTitle}
                            </p>
                          </div>
                          <div className="flex-shrink-0 ml-2 flex flex-col items-center space-y-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {index + 1}
                            </span>
                            {isVideoAvailable(video) ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleWatched(video);
                                }}
                                disabled={watchingVideo === video.videoId}
                                className={`text-xs px-2 py-1 rounded transition-all duration-300 ${
                                  video.is_watched
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                                } ${watchingVideo === video.videoId ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                {watchingVideo === video.videoId ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b border-current" />
                                ) : video.is_watched ? (
                                  <CheckCircle size={12} />
                                ) : (
                                  <Circle size={12} />
                                )}
                              </button>
                            ) : (
                              // Hide Donar button if: 1) No video selected initially, OR 2) This video is the currently selected video showing main Donar button
                              !selectedVideo || (selectedVideo && !isVideoAvailable(selectedVideo) && selectedVideo.videoId === video.videoId) ? null : (
                                <a
                                  href="https://donorbox.org/divinavoluntadapp"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className={`inline-flex items-center text-xs px-2 py-1 rounded transition-all duration-300 transform hover:scale-105 cursor-pointer ${
                                    darkMode 
                                      ? 'bg-yellow-500 hover:bg-yellow-400 text-black shadow-lg hover:shadow-xl' 
                                      : 'bg-yellow-600 hover:bg-yellow-500 text-white shadow-lg hover:shadow-xl'
                                  }`}
                                >
                                  <svg 
                                    className="w-3 h-3 mr-1" 
                                    fill="currentColor" 
                                    viewBox="0 0 20 20" 
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                  </svg>
                                  Donar
                                </a>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bible Reference Modal */}
      {bibleModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-4xl h-[90vh] rounded-lg shadow-xl ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Modal Header */}
            <div className={`flex items-center justify-between p-4 border-b ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`text-lg font-semibold ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                📖 Referencia Bíblica: {currentBibleReference}
              </h3>
              <button
                onClick={closeBibleDialog}
                className={`p-2 rounded-full hover:bg-opacity-10 transition-colors ${
                  darkMode ? 'hover:bg-white text-gray-300' : 'hover:bg-black text-gray-600'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="h-[calc(90vh-80px)] p-4">
              <iframe
                src={`https://www.bibliatodo.com/concordancia-biblica?s=${encodeURIComponent(currentBibleReference)}&version=JER&ant=tb`}
                className="w-full h-full border-0 rounded"
                title={`Concordancia Bíblica: ${currentBibleReference}`}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolumePage;